import { useEffect, useContext } from 'react';
import { AuthContext } from '@/components/context/AuthContext';
import { useLocation } from 'react-router-dom';
import { 
  initializeErrorTracking, 
  updateErrorContext, 
  trackPageView,
  trackAction,
  logError 
} from '@/utils/error-tracking';

let isInitialized = false;

/**
 * Hook to initialize and manage error tracking
 */
export function useErrorTracking() {
  // Use context directly to avoid circular dependency issues
  const authContext = useContext(AuthContext);
  
  // Only use location if we're inside a Router
  let location: any = null;
  try {
    location = useLocation();
  } catch (e) {
    // Not inside a Router yet, that's ok
  }
  
  useEffect(() => {
    // Initialize error tracking only once
    if (!isInitialized) {
      isInitialized = true;
      
      // Initialize with current auth context if available
      const authData = authContext ? {
        userId: authContext.user?.id,
        userEmail: authContext.user?.email,
        userRole: authContext.userRoles?.join(','),
        schoolId: authContext.userSchool?.id,
      } : undefined;
      
      initializeErrorTracking(authData);
    }
  }, []);
  
  // Update context when auth changes
  useEffect(() => {
    if (authContext && authContext.user) {
      updateErrorContext({
        userId: authContext.user.id,
        userEmail: authContext.user.email,
        userRole: authContext.userRoles?.join(','),
        schoolId: authContext.userSchool?.id,
      });
    }
  }, [authContext?.user, authContext?.userRoles, authContext?.userSchool]);
  
  // Track page navigation
  useEffect(() => {
    if (location) {
      trackPageView(location.pathname, {
        search: location.search,
        hash: location.hash
      });
    }
  }, [location]);
  
  return {
    trackAction,
    logError
  };
}

/**
 * Hook to track component-level errors
 */
export function useComponentErrorTracking(componentName: string) {
  const trackComponentError = (error: Error, errorInfo?: any) => {
    logError(error, {
      component: componentName,
      errorInfo
    });
  };
  
  const trackComponentAction = (action: string, data?: any) => {
    trackAction(action, componentName, data);
  };
  
  return {
    trackComponentError,
    trackComponentAction
  };
}